import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toaster } from './ui/toast';
import { S3ImageUpload } from './S3ImageUpload';
import { FormattedPhoneInput } from './FormattedPhoneInput';
import { useT } from '../i18n/admin';
import { BasicsError, PhoneChangeError, getUserAuditLog, requestPhoneChange, updateUserBasics, type UserBasics } from '../api/frontDeskUsers';
import { ProfilePictureError, generateProfilePicture } from '../services/documentAnalysis';
import { useFdCan } from '../hooks/useFdCan';
import { toChangeLog } from '../pages/Users/userChangeLog';

export interface BasicsUser {
  id: number | string;
  name?: string | null;
  last_name?: string | null;
  photo_url?: string | null;
  email?: string | null;
  tel?: string | null;
  pending_tel?: string | null;
  pending_tel_at?: string | null;
}

const DEFAULT_COUNTRY = { id: 'DO', label: 'Dominican Republic', dialCode: '+1' } as React.ComponentProps<typeof FormattedPhoneInput>['country'];

/** The new photo has no face the server can crop: nothing is saved. */
class NoFaceError extends Error {}

const basicsFailureText = (err: unknown): string => {
  if (err instanceof NoFaceError) return 'We could not find a face in that photo. Try another one.';
  if (err instanceof ProfilePictureError) return 'The photo could not be processed right now. Try again.';
  if (err instanceof BasicsError && err.code === 'NO_ACCOUNT') return "We could not find this person's sign-in account.";
  if (err instanceof BasicsError && err.code === 'DAILY_LIMIT') return 'You reached today\'s limit of photo changes. Try again tomorrow.';
  if (err instanceof BasicsError && err.code === 'REFUSED') return 'The server did not accept that change. If you changed the photo, upload it again and save right away.';
  return 'The basic data could not be saved. Try again.';
};

const phoneFailureText = (err: unknown): string => {
  if (err instanceof PhoneChangeError && err.code === 'PHONE_IN_USE') return 'That number already belongs to another account.';
  if (err instanceof PhoneChangeError && err.code === 'INVALID_PHONE') return 'Enter a valid number with its country code.';
  if (err instanceof PhoneChangeError && err.code === 'NO_ACCOUNT') return "We could not find this person's sign-in account.";
  return 'The phone change could not be requested. Try again.';
};

/**
 * Front Desk, on a user's page: edit the basic data (first name, last name, photo), ask for a new phone and read
 * who changed what and when. The phone is never changed here: it is left PENDING and the user confirms it by
 * SMS at their next login (Logic's contract, specs/front-desk-tenant-backend.md). The server accepts nothing
 * else from the Front Desk.
 */
export const FrontDeskUserBasics: React.FC<{ user: BasicsUser; onChanged?: () => void }> = ({ user, onChanged }) => {
  const { fmt } = useT();
  const [name, setName] = useState(user.name ?? '');
  const [lastName, setLastName] = useState(user.last_name ?? '');
  const [photo, setPhoto] = useState(user.photo_url ?? '');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [pending, setPending] = useState<string | null>(null);

  // A photo is saved only when there is a new one: clearing it is not a change the Front Desk route takes.
  const photoChanged = photo !== '' && photo !== (user.photo_url ?? '');
  const dirty = name !== (user.name ?? '') || lastName !== (user.last_name ?? '') || photoChanged;
  // A user always keeps a first name: a blank one is never sent (CreateResidentSheet asks for it the same way).
  const nameMissing = name.trim() === '';

  // The history shows when the server grants `view_audit_log` (the static matrix says yes for the Front Desk).
  const canSeeHistory = useFdCan('users', 'audit');
  const log = useQuery({
    queryKey: ['user-audit-log', user.id],
    queryFn: () => getUserAuditLog(user.id, { limit: 20 }),
    enabled: canSeeHistory,
    retry: false,
  });
  const entries = toChangeLog(log.data ?? []);

  const save = useMutation({
    mutationFn: async () => {
      const basics: UserBasics = {
        ...(name !== (user.name ?? '') ? { name } : {}),
        ...(lastName !== (user.last_name ?? '') ? { last_name: lastName } : {}),
      };
      // The photo and its cropped face (profile_picture) are saved together, so the face is cropped first.
      if (photoChanged) {
        const cropped = await generateProfilePicture(photo);
        if (!cropped) throw new NoFaceError();
        basics.photo_url = photo;
        basics.profile_picture = cropped;
      }
      return updateUserBasics(String(user.email ?? ''), basics);
    },
    onSuccess: () => {
      toaster.positive('Basic data saved.', { autoHideDuration: 3000 });
      log.refetch();
      onChanged?.();
    },
    onError: (err) => toaster.negative(basicsFailureText(err), { autoHideDuration: 5000 }),
  });

  const changePhone = useMutation({
    mutationFn: () => requestPhoneChange(String(user.email ?? ''), phone.trim()),
    onSuccess: (res) => {
      setPending(res.pendingPhone);
      setPhone('');
      toaster.positive('Requested. The user confirms the new number by SMS the next time they sign in.', { autoHideDuration: 6000 });
      log.refetch();
      onChanged?.();
    },
    onError: (err) => toaster.negative(phoneFailureText(err), { autoHideDuration: 6000 }),
  });

  const pendingLabel = pending ?? (user.pending_tel ? String(user.pending_tel) : null);
  const canRequestPhone = phone.replace(/\D/g, '').length >= 8 && Boolean(user.email) && !changePhone.isPending;

  return (
    <section aria-label="Edit basic data" className="mt-8 rounded-[12px] bg-white p-5" style={{ border: '1px solid var(--line)' }}>
      <h3 className="m-0 mb-3 text-[15px] font-bold" style={{ color: 'var(--ink-900)' }}>Edit basic data</h3>
      <div className="grid grid-cols-2 gap-x-3">
        <div className="fc" style={{ marginBottom: 12 }}>
          <label htmlFor="fdb-name">First name</label>
          <input id="fdb-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="fc" style={{ marginBottom: 12 }}>
          <label htmlFor="fdb-last">Last name</label>
          <input id="fdb-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <S3ImageUpload label="Photo" value={photo} onChange={setPhoto} />
      </div>
      <button type="button" className="btn primary" disabled={!dirty || nameMissing || !user.email || save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save'}
      </button>

      <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="text-[13px] font-semibold mb-1" style={{ color: 'var(--ink-700)' }}>Phone</div>
        <div className="text-[13px] mb-2" style={{ color: 'var(--ink-500)' }}>
          Now: {user.tel || 'none'}. A new number is not applied until the user confirms it by SMS; until then the current one keeps working.
        </div>
        {pendingLabel && (
          <div role="status" className="text-[13px] mb-2 rounded-[8px] px-3 py-2" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
            Waiting for the user to confirm {pendingLabel}
            {user.pending_tel_at ? ` (asked ${fmt.date(user.pending_tel_at)})` : ''}.
          </div>
        )}
        <FormattedPhoneInput value={phone} country={country} onChange={(v, c) => { setPhone(v); setCountry(c); }} placeholder="New phone number" showFormatHint={false} />
        <button type="button" className="btn ghost mt-2" disabled={!canRequestPhone} onClick={() => changePhone.mutate()}>
          {changePhone.isPending ? 'Requesting…' : 'Request phone change'}
        </button>
      </div>

      {canSeeHistory && (
      <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="text-[13px] font-semibold mb-2" style={{ color: 'var(--ink-700)' }}>Change history</div>
        {log.isLoading && <div className="text-[13px]" style={{ color: 'var(--ink-500)' }}>Loading…</div>}
        {!log.isLoading && entries.length === 0 && <div className="text-[13px]" style={{ color: 'var(--ink-500)' }}>No changes yet.</div>}
        <ul className="m-0 p-0 list-none flex flex-col gap-2">
          {entries.map((e) => (
            <li key={e.id} className="text-[13px]" style={{ color: 'var(--ink-700)' }}>
              <div style={{ color: 'var(--ink-500)' }}>{fmt.dateTime(e.when)} · {e.who}</div>
              {e.lines.map((l) => <div key={l}>{l}</div>)}
            </li>
          ))}
        </ul>
      </div>
      )}
    </section>
  );
};
